// src/components/ContactForm.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isPossiblePhoneNumber, getCountryCallingCode } from 'react-phone-number-input';
import { getSortedCountries, getCountryName, detectCountryFromLocale } from '../utils/phoneCountryUtils';
import { saveLeadSubmission } from '../utils/firebaseUtils';
import { trackEvent } from '../utils/Tracking';
import AdditionalInfoBox from './AdditionalInfoBox';
import { getUserTimezoneDisplay } from '../utils/timezoneUtilities';

const ContactForm = ({ 
  initialName = "", 
  selectedClinic = null,
  hideAdditionalInfo = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [name, setName] = useState(initialName);
  const [selectedCountry, setSelectedCountry] = useState(() => detectCountryFromLocale());
  const [phoneInput, setPhoneInput] = useState(""); // Local number only
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recommendationId, setRecommendationId] = useState(null);
  const inputRef = useRef(null);

  const { priority: priorityCountries, rest: restCountries } = getSortedCountries();

  // Get user's timezone for display
  const userTimezone = getUserTimezoneDisplay();

  // Get recommendation ID from URL if present
  useEffect(() => {
    const pathname = location.pathname;
    const recommendationPattern = /\/recommendations\/([^\/]+)/;
    const match = pathname.match(recommendationPattern);
    
    if (match && match[1]) {
      setRecommendationId(match[1]);
    }
  }, [location.pathname]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // 1) Name validation
    if (name.trim().length < 3) {
      setError("Name must be longer than 3 characters.");
      return;
    }

    // 2) Phone validation
    if (!phoneInput || !phoneInput.trim()) {
      setError("Please enter your phone number.");
      return;
    }

    // Build full phone number
    const dialCode = getCountryCallingCode(selectedCountry);
    const cleanedInput = phoneInput.replace(/^\+\d+/, '').trim();
    const fullPhone = `+${dialCode}${cleanedInput}`;

    if (!isPossiblePhoneNumber(fullPhone)) {
      setError("Please enter a valid phone number.");
      return;
    }

    setLoading(true);
    try {
      
      // Create a lead submission
      const leadSubmission = {
        name,
        phoneNumber: fullPhone,
        user_timezone: userTimezone,
        submittedAt: new Date().toISOString(),
        selectedClinic: selectedClinic || null,
        recommendation_id: recommendationId || null
      };

      // Save lead
      await saveLeadSubmission(leadSubmission);

      // Track the event (non-blocking)
      try {
        await trackEvent('contact_form_submitted', {
          user_name: name,
          phone_number: fullPhone,
          country_code: selectedCountry,
          user_timezone: userTimezone,
          selected_clinic: selectedClinic ? selectedClinic.title : null,
          recommendation_id: recommendationId || null
        });
      } catch (trackingError) {
        console.error('Error tracking form submission:', trackingError);
      }

      navigate("/thank-you");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className=" bg-white p-4 md:pt-7 md:pb-5 md:px-7 rounded-lg shadow-md border border-gray-200 md:shadow-gray-300 mt-6">
      {/* Title and Subtitle */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-600 text-left mb-1">
          Online Consultation with the Doctor
        </h2>
        <p className="text-lg text-gray-400 text-left mb-2">
          Consultations are quick, conducted via text or video with your doctor
        </p>
        
        {/* Divider line */}
        <div className="border-t border-gray-300 mb-4"></div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3 md:gap-4">
        {/* Name */}
        <input
          type="text"
          placeholder="Your Name"
          className="px-3 py-2 rounded-lg border border-gray-300 focus:ring focus:ring-blue-200 focus:border-blue-500 outline-none"
          value={name}
          onChange={e => setName(e.target.value)}
        />

        {/* Phone */}
        <div className="flex gap-2 items-center">
          {/* Country Selector - Shows flag only when closed */}
          <div className="relative flex-shrink-0 flex items-center gap-1">
            <select
              value={selectedCountry}
              onChange={(e) => {
                setSelectedCountry(e.target.value);
                if (error) setError('');
                if (inputRef.current) inputRef.current.focus();
              }}
              autoComplete="tel-country-code"
              className="phone-country-select h-12 w-12 cursor-pointer bg-transparent border-0 focus:outline-none appearance-none"
              style={{
                backgroundImage: `url(https://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedCountry}.svg)`,
                backgroundSize: '36px 24px',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                color: 'transparent',
              }}
            >
              {priorityCountries.map((country) => (
                <option key={country} value={country}>
                  {getCountryName(country)} +{getCountryCallingCode(country)}
                </option>
              ))}
              <option disabled>──────────────</option>
              {restCountries.map((country) => (
                <option key={country} value={country}>
                  {getCountryName(country)} +{getCountryCallingCode(country)}
                </option>
              ))}
            </select>
            {/* Dropdown arrow - positioned next to flag */}
            <div className="pointer-events-none -ml-2">
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                <path 
                  d="M1 1L5 5L9 1" 
                  stroke="#111827" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {/* Phone Input with Country Code Prefix */}
          <div className="flex-1 relative">
            <div className="flex items-center h-10 px-3 rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-500 transition-all">
              {/* Non-editable country code prefix */}
              <span className="font-medium text-gray-500 select-none pointer-events-none mr-1">
                +{getCountryCallingCode(selectedCountry)}
              </span>
              
              {/* Actual input field */}
              <input
                ref={inputRef}
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                name="phone-national"
                value={phoneInput}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/[^\d\s\-()]/g, '');
                  setPhoneInput(cleaned);
                  if (error) setError('');
                }}
                placeholder="555 123 4567"
                className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full text-xl font-black bg-mm_blue text-white p-3 rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Request Free Consultation"}
        </button>

        {/* Footer Text */}
        <div className="text-center text-md text-gray-400 -mt-2">
          <div>Consultations are conducted online via text</div>
          <div>You will not be charged for a consultation</div>
        </div>

        {/* Error */}
        {error && (
          <div className="mt-2 text-center text-sm text-red-600">
            {error}
          </div>
        )}
      </form>
      
      {/* Only render AdditionalInfoBox if hideAdditionalInfo is false */}
      {!hideAdditionalInfo && <AdditionalInfoBox />}
    </div>
  );
};

export default ContactForm;